import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n';
import { legalCopy } from './legalContent';
import { cgvPage } from './cgvContent';
import { openAnalyticsPreferences } from '../analytics';
import './LegalPages.css';

const LANGS = ['fr', 'en', 'pt'];

const LEGAL_NAV = {
  fr: [
    ['/mentions-legales', 'Mentions légales'],
    ['/politique-confidentialite', 'Confidentialité'],
    ['/cgu', 'CGU'],
    ['/cgv', 'CGV'],
  ],
  en: [
    ['/mentions-legales', 'Legal notice'],
    ['/politique-confidentialite', 'Privacy'],
    ['/cgu', 'Terms of use'],
    ['/cgv', 'Sales terms (FR)'],
  ],
  pt: [
    ['/mentions-legales', 'Aviso legal'],
    ['/politique-confidentialite', 'Privacidade'],
    ['/cgu', 'Termos de uso'],
    ['/cgv', 'Condições de venda (FR)'],
  ],
};

function LegalSection({ section, index }) {
  return (
    <section className="legal-card">
      <span className="legal-card-index">{String(index + 1).padStart(2, '0')}</span>
      <div className="legal-card-body">
        <h2>{section.title}</h2>

        {section.paragraphs?.map((paragraph) => (
          <p key={paragraph}>{paragraph}</p>
        ))}

        {section.definitions && (
          <div className="legal-definition-list">
            {section.definitions.map(([label, value]) => (
              <div className="legal-definition-item" key={label}>
                <span className="legal-label">{label}</span>
                <span className="legal-value">
                  {value === 'contact@alioune-seck.dev' ? (
                    <a href="mailto:contact@alioune-seck.dev">{value}</a>
                  ) : value === 'vercel.com' ? (
                    <a href="https://vercel.com" target="_blank" rel="noreferrer">{value}</a>
                  ) : (
                    value
                  )}
                </span>
              </div>
            ))}
          </div>
        )}

        {section.list && (
          <ul className="legal-list">
            {section.list.map((item) => <li key={item}>{item}</li>)}
          </ul>
        )}

        {section.after?.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
      </div>
    </section>
  );
}

export default function LegalPageLayout({ pageKey }) {
  const { lang, setLang } = useLang();
  const t = legalCopy[lang] || legalCopy.fr;
  const page = pageKey === 'cgv' ? cgvPage : t.pages[pageKey];
  const nav = LEGAL_NAV[lang] || LEGAL_NAV.fr;
  const activePath = pageKey === 'mentions'
    ? '/mentions-legales'
    : pageKey === 'privacy'
      ? '/politique-confidentialite'
      : '/' + pageKey;

  useEffect(() => {
    const suffix = pageKey === 'cgv' ? 'Conditions générales de vente' : page.title;
    document.title = `${suffix} | Alioune Seck`;
  }, [page.title, pageKey]);

  return (
    <main className="legal-page">
      <div className="legal-shell">
        <header className="legal-topbar">
          <Link to="/" className="legal-brand">
            <span className="legal-brand-symbol">AS</span>
            <span>
              <strong>Alioune Seck</strong>
              <small>{pageKey === 'cgv' ? 'Conditions de prestation' : t.brandRole}</small>
            </span>
          </Link>

          <div className="legal-top-actions">
            <Link to="/" className="legal-back-link">{t.back}</Link>
            <div className="legal-lang" aria-label={t.langLabel}>
              {LANGS.map((code) => (
                <button
                  key={code}
                  className={lang === code ? 'active' : ''}
                  onClick={() => setLang(code)}
                  type="button"
                  aria-current={lang === code ? 'true' : undefined}
                >
                  {code.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </header>

        <section className="legal-hero">
          <div>
            <span className="legal-kicker">{page.kicker}</span>
            <p className="legal-updated">{pageKey === 'cgv' ? 'Version 1.0 · 8 octobre 2026' : t.updated}</p>
          </div>
          <div>
            <h1>{page.title}</h1>
            <p className="legal-intro">{page.intro}</p>
            {pageKey === 'cgv' && (
              <p className="legal-notice">
                La version française constitue la version de référence. Ce document couvre les conditions générales de la prestation, mais un devis ou contrat peut prévoir des conditions particulières plus adaptées à un projet.
              </p>
            )}
          </div>
        </section>

        <div className="legal-content-grid">
          {page.sections.map((section, index) => (
            <LegalSection section={section} index={index} key={section.title} />
          ))}
        </div>

        <nav className="legal-bottom-nav" aria-label={t.legalNav}>
          {nav.map(([to, label]) => (
            <Link to={to} className={to === activePath ? 'legal-chip active' : 'legal-chip'} key={to}>
              {label}
            </Link>
          ))}
          <button type="button" className="legal-chip legal-cookie-button" onClick={openAnalyticsPreferences}>
            {lang === 'fr' ? 'Gérer les cookies' : lang === 'pt' ? 'Gerir cookies' : 'Cookie settings'}
          </button>
        </nav>
      </div>
    </main>
  );
}
